import { Controller } from "@hotwired/stimulus";
import { scrollSheetToTop } from "../utils/sheet_scroll";

// Two-step "add a preferred partner" flow inside the settings drawer (no visible
// step indicators): first search HSDS locations and pick one from the server-
// rendered cards, then optionally add a staff note before saving. Mirrors the
// referral wizard's directory search — fetches bare HTML cards and injects them.
export default class extends Controller {
  static targets = [
    "search",
    "results",
    "searchStep",
    "noteStep",
    "selectedCard",
    "locationId",
    "footer",
  ];
  static values = { searchUrl: String };

  declare readonly resultsTarget: HTMLElement;
  declare readonly searchStepTarget: HTMLElement;
  declare readonly noteStepTarget: HTMLElement;
  declare readonly selectedCardTarget: HTMLElement;
  declare readonly locationIdTarget: HTMLInputElement;
  declare readonly footerTarget: HTMLElement;
  declare readonly searchUrlValue: string;

  private hintHtml = "";
  private searchTimeout?: ReturnType<typeof setTimeout>;

  connect() {
    this.hintHtml = this.resultsTarget.innerHTML;
  }

  onSearch(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => this.doSearch(value), 300);
  }

  private async doSearch(query: string) {
    if (query.trim().length < 2) {
      this.resultsTarget.innerHTML = this.hintHtml;
      return;
    }
    const url = new URL(this.searchUrlValue, window.location.origin);
    url.searchParams.set("q", query);
    const response = await fetch(url.toString(), {
      headers: { Accept: "text/html" },
      credentials: "same-origin",
    });
    if (!response.ok) return;
    this.resultsTarget.innerHTML = await response.text();
  }

  // Delegated from the results container: a card click bubbles up here.
  select(event: Event) {
    const card = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-hsds-location-id]",
    );
    const locationId = card?.dataset.hsdsLocationId;
    if (!card || !locationId) return;

    this.locationIdTarget.value = locationId;
    this.selectedCardTarget.innerHTML = card.outerHTML;
    this.searchStepTarget.classList.add("hidden");
    this.noteStepTarget.classList.remove("hidden");
    this.footerTarget.classList.remove("hidden");
    scrollSheetToTop(this.element);
  }

  back() {
    this.locationIdTarget.value = "";
    this.noteStepTarget.classList.add("hidden");
    this.footerTarget.classList.add("hidden");
    this.searchStepTarget.classList.remove("hidden");
    scrollSheetToTop(this.element);
  }
}
